import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { CheckCircle, XCircle, AlertCircle } from 'lucide-react';

interface FormInputProps {
  id: string;
  name: string;
  type?: 'text' | 'email' | 'password';
  label: string;
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  validationState?: 'idle' | 'valid' | 'invalid';
  error?: string;
  success?: string;
  autoComplete?: string;
  className?: string;
}

const FormInput: React.FC<FormInputProps> = ({
  id,
  name,
  type = 'text',
  label,
  value,
  onChange,
  onBlur,
  placeholder,
  required = false,
  disabled = false,
  validationState = 'idle',
  error,
  success,
  autoComplete,
  className = ''
}) => {
  const [isFocused, setIsFocused] = useState(false);

  const getInputClasses = () => {
    const baseClasses = 'w-full px-4 py-3 bg-white dark:bg-zinc-800 border rounded-lg shadow-sm transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2';

    if (validationState === 'invalid' || error) {
      return `${baseClasses} border-red-300 dark:border-red-600 focus:ring-red-500 focus:border-red-500`;
    }

    if (validationState === 'valid' || success) {
      return `${baseClasses} border-green-300 dark:border-green-600 focus:ring-green-500 focus:border-green-500`;
    }

    if (isFocused) {
      return `${baseClasses} border-teal-300 dark:border-teal-600 focus:ring-teal-500 focus:border-teal-500`;
    }

    return `${baseClasses} border-zinc-300 dark:border-zinc-700 focus:ring-teal-500 focus:border-teal-500`;
  };

  const getIcon = () => {
    if (validationState === 'valid' || success) {
      return <CheckCircle className="w-5 h-5 text-green-500" />;
    }

    if (validationState === 'invalid' || error) {
      return <XCircle className="w-5 h-5 text-red-500" />;
    }

    return null;
  };

  return (
    <div className={`space-y-2 ${className}`}>
      <label
        htmlFor={id}
        className="block text-sm font-medium text-zinc-700 dark:text-zinc-300"
      >
        {label}
        {required && <span className="text-red-500 ml-1">*</span>}
      </label>

      <div className="relative">
        <input
          id={id}
          name={name}
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onBlur={() => {
            setIsFocused(false);
            onBlur?.();
          }}
          onFocus={() => setIsFocused(true)}
          placeholder={placeholder}
          required={required}
          disabled={disabled}
          autoComplete={autoComplete}
          className={getInputClasses()}
        />

        {getIcon() && (
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            className="absolute right-3 top-1/2 transform -translate-y-1/2"
          >
            {getIcon()}
          </motion.div>
        )}
      </div>

      {/* Error Message */}
      {(error || (validationState === 'invalid')) && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center space-x-2 text-red-600 dark:text-red-400 text-sm"
        >
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          <span>{error}</span>
        </motion.div>
      )}

      {/* Success Message */}
      {success && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center space-x-2 text-green-600 dark:text-green-400 text-sm"
        >
          <CheckCircle className="w-4 h-4 flex-shrink-0" />
          <span>{success}</span>
        </motion.div>
      )}
    </div>
  );
};

export default FormInput;
